import { useEffect, useState } from "react";
import type { MediaDTO } from "@wedding/shared-types";
import { downloadUrl, deleteOwnMedia } from "../../api/media.js";
import { VideoPlayer } from "./VideoPlayer.js";
import { DownloadButton } from "./DownloadButton.js";

interface LightboxProps {
  items: MediaDTO[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onDeleted?: (mediaId: string) => void;
}

export function Lightbox({ items, index, onClose, onNavigate, onDeleted }: LightboxProps) {
  const media = items[index];
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!media || deleting) return;
    if (!window.confirm("Supprimer définitivement cet envoi ?")) return;
    setDeleting(true);
    try {
      await deleteOwnMedia(media.id);
      onDeleted?.(media.id);
    } catch {
      window.alert("Échec de la suppression, réessaie.");
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
      if (e.key === "ArrowRight" && index < items.length - 1) onNavigate(index + 1);
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index, items.length, onClose, onNavigate]);

  if (!media) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--color-overlay)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-2)",
          padding: "var(--space-3)",
        }}
      >
        <span style={{ color: "#fff", opacity: 0.85, fontSize: "0.9rem" }}>
          {media.uploaderName ? `Envoyé par ${media.uploaderName}` : ""}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <DownloadButton mediaId={media.id} className="btn btn-primary" />
        {media.canDelete && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              void handleDelete();
            }}
            disabled={deleting}
          >
            {deleting ? "Suppression…" : "Supprimer"}
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
        </div>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          padding: "0 var(--space-4)",
          minHeight: 0,
        }}
      >
        {index > 0 && (
          <NavButton side="left" onClick={() => onNavigate(index - 1)} />
        )}

        {media.type === "video" ? (
          <VideoPlayer mediaId={media.id} />
        ) : (
          <img
            src={downloadUrl(media.id)}
            alt={media.originalName}
            style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
          />
        )}

        {index < items.length - 1 && (
          <NavButton side="right" onClick={() => onNavigate(index + 1)} />
        )}
      </div>
    </div>
  );
}

function NavButton({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={side === "left" ? "Précédent" : "Suivant"}
      style={{
        position: "absolute",
        [side]: 8,
        top: "50%",
        transform: "translateY(-50%)",
        width: 48,
        height: 48,
        borderRadius: "50%",
        border: "none",
        background: "rgba(255,255,255,0.15)",
        color: "#fff",
        fontSize: "1.5rem",
        cursor: "pointer",
      }}
    >
      {side === "left" ? "‹" : "›"}
    </button>
  );
}
